// Static blog content. Each post is a long-form SEO article targeting a specific
// query family. Add new entries here and they appear automatically on /blog and
// in the sitemap (scripts/generate-sitemap.ts mirrors this list).

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  city?: string;
  category: "guide-ville" | "tarifs" | "demarches" | "conseils";
  publishedAt: string; // ISO date
  updatedAt?: string;
  readingMinutes: number;
  /** Markdown-lite content rendered as HTML (paragraphs, ##/### headings, lists, **bold**) */
  body: string;
  faq?: { question: string; answer: string }[];
}

const today = "2026-05-15";

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "trouver-baby-sitter-paris",
    title: "Trouver une baby-sitter à Paris : le guide complet 2026",
    description:
      "Comment choisir une baby-sitter à Paris : tarifs moyens, démarches CAF, vérifications à faire et arrondissements les plus demandés.",
    city: "Paris",
    category: "guide-ville",
    publishedAt: today,
    readingMinutes: 8,
    body: `## Pourquoi Paris est un cas particulier pour la garde d'enfants

Paris concentre la plus forte demande de **baby-sitting** de France : familles bi-actives, sorties tardives, écoles éloignées. Les **horaires atypiques** (soirées après 20 h, week-ends, vacances scolaires) y représentent près de 60 % des réservations.

## Tarifs moyens d'une baby-sitter à Paris en 2026

- **Garde ponctuelle en soirée** : 12 à 15 € de l'heure net
- **Garde régulière périscolaire** : 11 à 13 € de l'heure
- **Garde de nuit** : 80 à 120 € la nuit forfait
- **Sortie d'école + devoirs** : 13 à 16 € (anglophone : +2 €)

À Paris intra-muros, la sur-tarification peut atteindre **+15 %** par rapport à la moyenne nationale. Les arrondissements 6, 7, 8, 16 et 17 affichent les tarifs les plus élevés.

## Les démarches à connaître avant d'embaucher

1. **Crédit d'impôt service à la personne** (CESU) : 50 % des sommes versées sont déductibles.
2. **Déclaration Pajemploi** obligatoire si la garde est régulière à votre domicile.
3. **Vérification d'identité** : pièce d'identité, casier judiciaire (bulletin n°3) sur demande.
4. **Assurance responsabilité civile** : vérifier qu'elle couvre les accidents domestiques.

## Comment trouver rapidement à Paris

Click&Gard met en relation les parents parisiens avec des **profils vérifiés** (identité + selfie). Les pros disponibles dans votre arrondissement sont géolocalisés et vous voyez leur disponibilité en temps réel sur la grille hebdomadaire.

## Quartiers les plus demandés

Marais (3e/4e), Batignolles (17e), Bastille (11e), Buttes-Chaumont (19e) et le 15e concentrent la majorité des demandes de baby-sitting régulier.`,
    faq: [
      {
        question: "Quel est le tarif moyen d'une baby-sitter à Paris ?",
        answer:
          "Entre 12 et 15 € de l'heure net pour une garde ponctuelle en soirée. La garde régulière périscolaire se situe plutôt entre 11 et 13 €.",
      },
      {
        question: "Faut-il déclarer la baby-sitter ?",
        answer:
          "Oui, dès lors que la garde est régulière à votre domicile, la déclaration Pajemploi est obligatoire. Vous bénéficiez alors d'un crédit d'impôt de 50 %.",
      },
      {
        question: "Comment vérifier le sérieux d'une baby-sitter ?",
        answer:
          "Demandez la pièce d'identité, le bulletin n°3 du casier judiciaire et privilégiez une plateforme qui vérifie l'identité comme Click&Gard.",
      },
    ],
  },
  {
    slug: "trouver-baby-sitter-lyon",
    title: "Baby-sitting à Lyon : tarifs, quartiers et démarches en 2026",
    description:
      "Tout pour trouver une baby-sitter à Lyon : prix par quartier, démarches Pajemploi, conseils pour la rentrée et les vacances scolaires.",
    city: "Lyon",
    category: "guide-ville",
    publishedAt: today,
    readingMinutes: 7,
    body: `## La demande à Lyon en 2026

Lyon est la deuxième métropole française pour la demande de **garde d'enfants**. La concentration des cadres dans la Part-Dieu et Confluence génère une forte demande en garde **périscolaire 16h30-19h** et en **soirée**.

## Tarifs moyens d'une baby-sitter à Lyon

- Garde ponctuelle : **10 à 13 €/h net**
- Périscolaire régulier : **10 à 12 €/h**
- Garde de week-end : **12 à 14 €/h**
- Aide aux devoirs (CP-CM2) : **+1 à 2 €/h**

Les 2e, 6e et 7e arrondissements concentrent les tarifs les plus élevés. Villeurbanne et Caluire restent plus accessibles.

## Démarches à Lyon

Comme partout en France, la **déclaration Pajemploi** s'applique pour la garde régulière à domicile. La métropole de Lyon propose en complément une **aide CAF "garde au domicile"** sous conditions de ressources : à vérifier sur caf.fr.

## Trouver vite et bien

Sur Click&Gard, filtrez les pros disponibles à Lyon par **arrondissement, créneau et type de garde** (à votre domicile, chez le pro ou en extérieur).`,
    faq: [
      {
        question: "Quel tarif horaire à Lyon ?",
        answer: "Entre 10 et 13 € de l'heure net en moyenne, plus à Lyon 2/6/7.",
      },
      {
        question: "La CAF peut-elle aider ?",
        answer:
          "Oui, sous conditions de ressources via le complément libre choix du mode de garde (CMG). Vérifiez votre éligibilité sur caf.fr.",
      },
    ],
  },
  {
    slug: "trouver-baby-sitter-marseille",
    title: "Baby-sitter à Marseille : prix, quartiers et conseils 2026",
    description:
      "Guide complet pour trouver une baby-sitter à Marseille : tarifs par arrondissement, démarches CAF et conseils pour les sorties d'école.",
    city: "Marseille",
    category: "guide-ville",
    publishedAt: today,
    readingMinutes: 6,
    body: `## Le marché du baby-sitting à Marseille

Marseille combine forte demande **estivale** (touristes, familles en vacances), périscolaire toute l'année et besoins ponctuels en soirée. Les 6e, 7e, 8e et 9e arrondissements représentent près de la moitié des réservations.

## Tarifs moyens

- Garde ponctuelle : **9 à 12 €/h**
- Périscolaire : **9 à 11 €/h**
- Garde week-end : **11 à 14 €/h**

## Conseils pour la rentrée

Réservez votre baby-sitter régulière **avant fin août** : la rentrée scolaire concentre 70 % des demandes périscolaires sur 15 jours. Privilégiez un profil vérifié et fixez les règles dès la première rencontre.`,
    faq: [
      {
        question: "Combien coûte une baby-sitter à Marseille ?",
        answer: "Entre 9 et 12 € de l'heure pour une garde ponctuelle en soirée.",
      },
    ],
  },
  {
    slug: "tarifs-baby-sitting-france-2026",
    title: "Tarifs baby-sitting en France 2026 : le barème complet",
    description:
      "Combien payer une baby-sitter en France ? Tarifs nationaux par type de garde, comparaison par ville et règles SMIC + Pajemploi.",
    category: "tarifs",
    publishedAt: today,
    readingMinutes: 9,
    body: `## Le barème national 2026

Les **tarifs baby-sitting** en France varient selon trois critères : la **ville**, le **type de garde** et l'**expérience** du pro.

### Tarifs horaires nets moyens

| Type de garde | Tarif net moyen |
|---|---|
| Garde ponctuelle en soirée | 10-15 €/h |
| Garde régulière périscolaire | 10-13 €/h |
| Garde de nuit (forfait) | 70-120 €/nuit |
| Garde week-end | 11-14 €/h |
| Aide aux devoirs | +1-3 €/h |

### Variation par ville

- **Paris** : +15 % vs moyenne nationale
- **Lyon, Bordeaux, Nice** : +5 à 10 %
- **Toulouse, Nantes, Rennes** : moyenne nationale
- **Lille, Marseille, Montpellier** : -5 % environ

## Le SMIC horaire et le minimum légal

Le SMIC horaire 2026 est de **11,88 €/h brut** (à confirmer selon dernière revalorisation). En garde régulière à domicile, ce minimum s'applique, majoré de **10 %** au titre des congés payés.

## Crédit d'impôt et aides

- **Crédit d'impôt 50 %** pour services à la personne (plafond 12 000 €/an + 1 500 €/enfant)
- **CMG (CAF)** : prise en charge partielle selon ressources
- **Pajemploi** : déclaration en ligne, calcul automatique des cotisations

## Comment fixer le bon tarif

1. Comparez avec les profils Click&Gard de votre ville
2. Tenez compte de l'**expérience** (BAFA, diplôme petite enfance)
3. Ajustez si **horaires atypiques** (nuit, jours fériés : +25 %)
4. Le **transport** peut être pris en charge si pro à domicile`,
    faq: [
      {
        question: "Combien coûte une baby-sitter en France en 2026 ?",
        answer:
          "En moyenne 10 à 15 € de l'heure net, selon la ville et le type de garde. Paris est environ 15 % plus cher que la moyenne nationale.",
      },
      {
        question: "Y a-t-il un tarif minimum légal ?",
        answer:
          "Oui, le SMIC horaire majoré de 10 % au titre des congés payés s'applique pour les gardes régulières déclarées via Pajemploi.",
      },
    ],
  },
  {
    slug: "demarches-pajemploi-cesu-baby-sitter",
    title: "Pajemploi, CESU, déclaration : toutes les démarches d'une garde d'enfants",
    description:
      "Comprendre Pajemploi, le CESU, le crédit d'impôt et la déclaration de votre baby-sitter en 5 minutes. Guide pratique pour les parents.",
    category: "demarches",
    publishedAt: today,
    readingMinutes: 10,
    body: `## Quel statut pour ma baby-sitter ?

Trois cas existent en France :

1. **Garde régulière à votre domicile** → déclaration **Pajemploi** obligatoire
2. **Garde occasionnelle** (quelques heures) → **CESU** simplifie la déclaration
3. **Garde chez le pro** (assistante maternelle agréée) → contrat spécifique, **PMI**

## Pajemploi en 5 étapes

1. Créez votre compte sur **pajemploi.urssaf.fr**
2. Saisissez les heures effectuées chaque mois
3. Pajemploi calcule **automatiquement** les cotisations
4. La rémunération est versée par vous, les cotisations prélevées par l'URSSAF
5. Le **crédit d'impôt 50 %** est appliqué sur votre déclaration de revenus

## CESU déclaratif

Plus simple pour les **gardes ponctuelles** (moins de 8 h/semaine régulièrement). Vous saisissez le salaire net versé, le système calcule les cotisations.

## Crédit d'impôt service à la personne

- **50 %** des sommes versées (salaire + cotisations)
- Plafond : **12 000 €/an** + 1 500 € par enfant à charge
- S'applique aux deux déclarations (Pajemploi et CESU)

## Aides CAF complémentaires

Le **Complément Libre Choix du Mode de Garde** (CMG) prend en charge une partie des cotisations sociales selon vos ressources et l'âge de l'enfant.

## Bonnes pratiques

- Faire signer un **contrat de travail** dès la première heure
- Conserver les **preuves de paiement**
- Demander la pièce d'identité et le casier judiciaire (bulletin 3)
- Préciser la **durée des congés payés** (10 % du brut)`,
    faq: [
      {
        question: "Faut-il obligatoirement déclarer une baby-sitter ?",
        answer:
          "Oui, dès qu'il y a rémunération régulière. CESU ou Pajemploi selon la fréquence. Le travail dissimulé expose à des sanctions importantes.",
      },
      {
        question: "Quelle différence entre CESU et Pajemploi ?",
        answer:
          "CESU pour la garde occasionnelle ou domestique générale, Pajemploi spécifiquement pour la garde d'enfants à domicile.",
      },
    ],
  },
  {
    slug: "nounou-baby-sitter-difference",
    title: "Nounou, baby-sitter, assistante maternelle : quelles différences ?",
    description:
      "Quelle est la différence entre nounou, baby-sitter et assistante maternelle ? Statut, tarif, lieu de garde : le guide pour bien choisir.",
    category: "conseils",
    publishedAt: today,
    readingMinutes: 6,
    body: `## Trois métiers, trois cadres

Le mot **nounou** est familier — il regroupe en réalité trois statuts juridiques différents.

### 1. Baby-sitter

- **Garde ponctuelle** (soirée, week-end, vacances)
- Souvent étudiante ou jeune adulte
- Pas de diplôme requis (un BAFA est un plus)
- Tarif : **10 à 15 €/h net**

### 2. Garde d'enfants à domicile (nounou)

- **Garde régulière** chez vous (10-30 h/semaine)
- Statut salarié déclaré via **Pajemploi**
- Souvent CAP petite enfance ou expérience
- Tarif : **11 à 14 €/h net**

### 3. Assistante maternelle agréée

- Garde **chez elle** (jusqu'à 4 enfants)
- **Agrément PMI** obligatoire (formation 120 h)
- Contrat spécifique, conventions collectives
- Tarif : **3 à 5 €/h** par enfant + frais d'entretien

## Comment choisir ?

| Besoin | Recommandation |
|---|---|
| Sortie ponctuelle | Baby-sitter |
| Périscolaire régulier | Garde à domicile (nounou) |
| Garde toute la journée | Assistante maternelle |
| Bébé < 1 an, plein temps | Crèche ou ass. mat. |

Sur **Click&Gard**, vous filtrez par type de garde et par lieu (à votre domicile, chez le pro, en extérieur).`,
    faq: [
      {
        question: "Une nounou doit-elle être diplômée ?",
        answer:
          "Pour une garde régulière, le CAP petite enfance est recommandé mais pas obligatoire. Pour une assistante maternelle, l'agrément PMI est en revanche obligatoire.",
      },
    ],
  },
];

export const BLOG_CATEGORIES: { id: BlogPost["category"]; label: string }[] = [
  { id: "guide-ville", label: "Guides par ville" },
  { id: "tarifs", label: "Tarifs" },
  { id: "demarches", label: "Démarches" },
  { id: "conseils", label: "Conseils" },
];

export const findPostBySlug = (slug: string) =>
  BLOG_POSTS.find((p) => p.slug === slug);
